import api from './api';

/** Geld liegt ueberall in Cent, Mengen in Hundertsteln. Die Feldnamen sagen es. */

export interface Product {
  id: string;
  sku?: string | null;
  name: string;
  description?: string | null;
  unit: string;
  unitPriceCents: number;
  currency: string;
  taxRate: number;
  active: boolean;
  // --- Artikelstamm (ERP, ADR 0008) ---
  /** Physischer Artikel mit Lagerbestand (sonst Dienstleistung). */
  istLagerartikel?: boolean;
  /** Bestand wird ueber Lagerbewegungen gefuehrt; Abgang unter null wird abgewiesen. */
  bestandsfuehrung?: boolean;
  /** Warnschwelle in Hundertsteln, 0 = keine Warnung. */
  mindestbestandHundert?: number;
  einkaufspreisCents?: number;
  erloesKontoNr?: string | null;
  aufwandKontoNr?: string | null;
}

export interface QuoteItem {
  id: string;
  quoteId: string;
  productId?: string | null;
  description: string;
  unit: string;
  quantityHundert: number;
  unitPriceCents: number;
  discountPercent: number;
  taxRate: number;
  position: number;
}

export interface Quote {
  id: string;
  number: string;
  title?: string | null;
  /** draft | sent | accepted | declined | expired */
  status: string;
  issuedAt?: string | null;
  validUntil?: string | null;
  sentAt?: string | null;
  acceptedAt?: string | null;
  declinedAt?: string | null;
  declineReason?: string | null;
  introText?: string | null;
  termsText?: string | null;
  discountPercent: number;
  currency: string;
  netCents: number;
  taxCents: number;
  grossCents: number;
  dealId?: string | null;
  companyId?: string | null;
  leadId?: string | null;
  items?: QuoteItem[];
  company?: {
    id: string; name: string; domain?: string | null;
    address?: string | null; postalCode?: string | null; city?: string | null;
  } | null;
  deal?: { id: string; title: string; value?: number } | null;
  /** Auftraege aus diesem Angebot (Liste und Detail). */
  orders?: { id: string; number?: string | null; status: string }[];
}

export const STATUS_LABEL: Record<string, string> = {
  draft: 'Entwurf',
  sent: 'Verschickt',
  accepted: 'Angenommen',
  declined: 'Abgelehnt',
  expired: 'Abgelaufen',
};

export const EINHEITEN = ['Tag', 'Stunde', 'Stueck', 'Monat', 'Pauschale'];

/** Cent als deutscher Betrag. */
export const euro = (cents: number, waehrung = 'EUR') =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: waehrung }).format((cents || 0) / 100);

/** Hundertstel als lesbare Menge. */
export const menge = (hundert: number) =>
  new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format((hundert || 0) / 100);

/** Eingabe "1.400,50" -> 140050 Cent. Beide Trennzeichen erlaubt. */
export const centsAus = (eingabe: string): number => {
  const sauber = String(eingabe).trim().replace(/\./g, '').replace(',', '.');
  const zahl = Number(sauber);
  return Number.isFinite(zahl) ? Math.round(zahl * 100) : 0;
};

const quoteService = {
  // --- Leistungskatalog ---
  async listProducts(nurAktive = false): Promise<Product[]> {
    const response = await api.get('/angebote/produkte', { params: { nurAktive } });
    return response.data;
  },
  async createProduct(daten: Partial<Product>): Promise<Product> {
    const response = await api.post('/angebote/produkte', daten);
    return response.data;
  },
  async updateProduct(id: string, daten: Partial<Product>): Promise<Product> {
    const response = await api.patch(`/angebote/produkte/${id}`, daten);
    return response.data;
  },
  /** Preisliste von SecTepe.Core (Pakete, Einrichtung, Zusatzleistungen) anlegen oder nachziehen. */
  async corePreislisteUebernehmen(): Promise<{ angelegt: number; aktualisiert: number; unveraendert: number }> {
    const response = await api.post('/angebote/produkte/core-preisliste');
    return response.data;
  },

  // --- Angebote ---
  async list(filter?: { status?: string; dealId?: string; companyId?: string }): Promise<Quote[]> {
    const response = await api.get('/angebote', { params: filter || {} });
    return response.data;
  },
  async get(id: string): Promise<Quote> {
    const response = await api.get(`/angebote/${id}`);
    return response.data;
  },
  async create(daten: Partial<Quote>): Promise<Quote> {
    const response = await api.post('/angebote', daten);
    return response.data;
  },
  async update(id: string, daten: Partial<Quote>): Promise<Quote> {
    const response = await api.patch(`/angebote/${id}`, daten);
    return response.data;
  },
  async setStatus(id: string, status: string, grund?: string): Promise<Quote> {
    const response = await api.post(`/angebote/${id}/status`, { status, grund });
    return response.data;
  },
  async abgelaufene(): Promise<{ abgelaufen: number }> {
    const response = await api.post('/angebote/abgelaufene');
    return response.data;
  },

  // --- Positionen ---
  async addItem(quoteId: string, daten: Partial<QuoteItem>): Promise<Quote> {
    const response = await api.post(`/angebote/${quoteId}/positionen`, daten);
    return response.data;
  },
  async updateItem(itemId: string, daten: Partial<QuoteItem>): Promise<Quote> {
    const response = await api.patch(`/angebote/positionen/${itemId}`, daten);
    return response.data;
  },
  async removeItem(itemId: string): Promise<Quote> {
    const response = await api.delete(`/angebote/positionen/${itemId}`);
    return response.data;
  },

  /** Das PDF laeuft ueber denselben authentifizierten Weg wie alles andere. */
  async pdfOeffnen(id: string): Promise<void> {
    const response = await api.get(`/angebote/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
    // Erst freigeben, wenn der neue Tab die Datei geholt hat.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },
};

export default quoteService;
